import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocale } from "next-intl";
import { conversations as api } from "@/lib/api/conversations";
import type { ChatMessage, ConversationSummary } from "@/lib/api/conversations";
import { ApiError } from "@/lib/api/transport";
import { useAuth, useHasToolAccess } from "@/hooks/useAuth";
import {
  useUserChannel,
  type UserChannelMessage,
} from "@/hooks/useUserChannel";

// Slim Guide chat state machine. Subscribes to the user's UserChannel
// and reduces the agent:* event vocabulary into a single `entries[]`
// list the page renders. Streaming chunks accumulate into the in-flight
// assistant entry; the terminal `agent:message` claims it with the
// persisted content. Tool-call / consent / observation events from the
// Empathy version are intentionally absent — Guide doesn't emit them.

export type ChatEntry =
  | {
      type: "user";
      id: number;
      content: string;
    }
  | {
      type: "assistant";
      id: number | null;
      content: string;
      streaming: boolean;
      interrupted?: boolean;
    };

interface GuideChatValue {
  conversationId: number | null;
  entries: ChatEntry[];
  loading: boolean;
  error: string | null;
  conversations: ReadonlyArray<ConversationSummary>;
  conversationsLoading: boolean;
  send: (
    text: string,
    clientContext?: Record<string, unknown>,
  ) => Promise<void>;
  loadConversation: (id: number) => Promise<void>;
  refreshConversations: () => Promise<void>;
  deleteConversation: (id: number) => Promise<void>;
  reset: () => void;
  interrupt: () => void;
}

const GuideChatContext = createContext<GuideChatValue>(null!);

export function GuideChatProvider({ children }: { children: ReactNode }) {
  const { status: authStatus } = useAuth();
  const hasAccess = useHasToolAccess();
  const channel = useUserChannel();
  const locale = useLocale();

  const [conversationId, setConversationId] = useState<number | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<
    ReadonlyArray<ConversationSummary>
  >([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Track which conversation the cable subscription cares about — the
  // singleton consumer fans events to every handler and we filter
  // client-side by conversation_id, the same way Empathy does.
  const conversationIdRef = useRef<number | null>(null);
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // ── Conversation list ───────────────────────────────────────────
  const refreshConversations = useCallback(async () => {
    if (authStatus !== "authenticated") return;
    setConversationsLoading(true);
    try {
      const list = await api.listConversations();
      setConversations(list);
    } catch {
      // Best-effort — leave the existing list in place on failure.
    } finally {
      setConversationsLoading(false);
    }
  }, [authStatus]);

  // Initial load + refresh on auth changes.
  useEffect(() => {
    if (authStatus !== "authenticated") {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setConversations([]);
      return;
    }
    void refreshConversations();
  }, [authStatus, refreshConversations]);

  // ── Cable handler ───────────────────────────────────────────────
  useEffect(() => {
    if (!channel) return;
    return channel.subscribe((msg: UserChannelMessage) => {
      if (
        msg.conversation_id != null &&
        msg.conversation_id !== conversationIdRef.current
      ) {
        return;
      }
      handleCableEvent(msg, setEntries, setLoading, setError, () => {
        // After a turn finalizes, refresh the list so the new
        // conversation surfaces and any auto-titled summary lands. The
        // title job is async on the backend, so a single delayed retry
        // catches the title once the small LLM call returns.
        void refreshConversations();
        setTimeout(() => void refreshConversations(), 4000);
      });
    });
  }, [channel, refreshConversations]);

  // ── Sender ──────────────────────────────────────────────────────
  const send = useCallback(
    async (text: string, clientContext?: Record<string, unknown>) => {
      const trimmed = text.trim();
      if (!trimmed || loading) return;
      if (!hasAccess) {
        setError("An active membership is required to chat.");
        return;
      }

      setError(null);
      setLoading(true);

      // Auto-inject locale + current route + a per-call timestamp so the
      // GuideContext builder sees the legacy app's grounding signals
      // without each caller having to reassemble them.
      const enrichedContext = {
        route: typeof window !== "undefined" ? window.location.pathname : null,
        locale,
        ...(clientContext ?? {}),
      };

      try {
        const reply = conversationId
          ? await api.sendChatMessage(conversationId, trimmed, enrichedContext)
          : await api.createConversation(trimmed, enrichedContext);

        setConversationId(reply.conversation_id);
        setEntries((prev) => [
          ...prev,
          {
            type: "user",
            id: reply.message.id,
            content: reply.message.content,
          },
        ]);
      } catch (e) {
        setLoading(false);
        if (e instanceof ApiError && e.status === 402) {
          setError("An active membership is required to chat.");
        } else {
          setError("Guide didn't reply. Try again.");
        }
      }
    },
    [conversationId, loading, hasAccess, locale],
  );

  // ── Load history (optional — drives "open prior conversation") ──
  const loadConversation = useCallback(async (id: number) => {
    setError(null);
    setLoading(true);
    try {
      const detail = await api.showConversation(id);
      setConversationId(detail.conversation_id);
      setEntries(
        detail.messages
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map(messageToEntry),
      );
    } catch {
      setError("Couldn't load that conversation.");
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setConversationId(null);
    setEntries([]);
    setError(null);
    setLoading(false);
  }, []);

  const interrupt = useCallback(() => {
    if (!channel || !conversationId) return;
    channel.perform("interrupt", { conversation_id: conversationId });
  }, [channel, conversationId]);

  const deleteConversation = useCallback(async (id: number) => {
    try {
      await api.destroyConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (conversationIdRef.current === id) {
        setConversationId(null);
        setEntries([]);
      }
    } catch {
      // Surface failures via the conversations list staying intact;
      // the next refresh will reconcile.
    }
  }, []);

  const value = useMemo<GuideChatValue>(
    () => ({
      conversationId,
      entries,
      loading,
      error,
      conversations,
      conversationsLoading,
      send,
      loadConversation,
      refreshConversations,
      deleteConversation,
      reset,
      interrupt,
    }),
    [
      conversationId,
      entries,
      loading,
      error,
      conversations,
      conversationsLoading,
      send,
      loadConversation,
      refreshConversations,
      deleteConversation,
      reset,
      interrupt,
    ],
  );

  return (
    <GuideChatContext.Provider value={value}>
      {children}
    </GuideChatContext.Provider>
  );
}

export function useGuideChat() {
  return useContext(GuideChatContext);
}

// ── Cable-event reducer ─────────────────────────────────────────────

function handleCableEvent(
  msg: UserChannelMessage,
  setEntries: React.Dispatch<React.SetStateAction<ChatEntry[]>>,
  setLoading: (b: boolean) => void,
  setError: (e: string | null) => void,
  onTurnFinal?: () => void,
) {
  switch (msg.event) {
    case "agent:thinking":
      setLoading(true);
      return;

    case "agent:stream_start": {
      const id = (msg.message_id as number | null) ?? null;
      setEntries((prev) => [
        ...prev,
        { type: "assistant", id, content: "", streaming: true },
      ]);
      return;
    }

    case "agent:chunk": {
      const delta = typeof msg.delta === "string" ? msg.delta : "";
      if (!delta) return;
      setEntries((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.type === "assistant" && last.streaming) {
          const next = [...prev];
          next[next.length - 1] = { ...last, content: last.content + delta };
          return next;
        }
        return [
          ...prev,
          {
            type: "assistant",
            id: (msg.message_id as number | null) ?? null,
            content: delta,
            streaming: true,
          },
        ];
      });
      return;
    }

    case "agent:message": {
      const m = (msg.message as ChatMessage | undefined) ?? null;
      if (!m) return;
      const final = msg.final !== false;
      setEntries((prev) => {
        const last = prev[prev.length - 1];
        if (
          last &&
          last.type === "assistant" &&
          (last.streaming || last.id === m.id || last.id == null)
        ) {
          const next = [...prev];
          next[next.length - 1] = {
            type: "assistant",
            id: m.id,
            content: m.content,
            streaming: !final,
          };
          return next;
        }
        return [
          ...prev,
          {
            type: "assistant",
            id: m.id,
            content: m.content,
            streaming: !final,
          },
        ];
      });
      if (final) {
        setLoading(false);
        onTurnFinal?.();
      }
      return;
    }

    case "agent:interrupted":
      setEntries((prev) => {
        const last = prev[prev.length - 1];
        if (last && last.type === "assistant") {
          const next = [...prev];
          next[next.length - 1] = {
            ...last,
            streaming: false,
            interrupted: true,
          };
          return next;
        }
        return prev;
      });
      setLoading(false);
      return;

    case "agent:error":
      setError(
        typeof msg.error === "string"
          ? msg.error
          : "Guide didn't reply. Try again.",
      );
      setLoading(false);
      return;
  }
}

function messageToEntry(m: ChatMessage): ChatEntry {
  if (m.role === "user") {
    return { type: "user", id: m.id, content: m.content };
  }
  return { type: "assistant", id: m.id, content: m.content, streaming: false };
}
